import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Upload, X, FileText, Image as ImageIcon } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const TYPE_META = {
  return: { label: "Return Request", intro: "Request a return of your order or an item." },
  cancellation: { label: "Cancellation Request", intro: "Cancel an order that hasn't shipped yet." },
  inr: { label: "Item Not Received", intro: "Your order didn't arrive as expected." },
};

const MAX_ATTACHMENTS = 5;

function AttachmentChip({ att, onRemove }) {
  const isImg = (att.content_type || "").startsWith("image/");
  return (
    <div className="flex items-center gap-2 px-2 py-1 border border-[#E5E1D8] bg-[#F9F6F0]">
      {isImg ? <ImageIcon size={13} /> : <FileText size={13} />}
      <span className="font-mono text-[10px] uppercase tracking-editorial truncate max-w-[160px]">{att.filename}</span>
      {onRemove && (
        <button type="button" onClick={onRemove} className="text-[#7A2021]" aria-label="Remove">
          <X size={12} />
        </button>
      )}
    </div>
  );
}

export default function NewCaseDialog({ open, onOpenChange, order, onCreated }) {
  const [type, setType] = useState("");
  const [reasons, setReasons] = useState({ return: [], cancellation: [], inr: [] });
  const [reason, setReason] = useState("");
  const [comments, setComments] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (open) {
      setType("");
      setReason("");
      setComments("");
      setAttachments([]);
      if (!reasons.return.length) {
        api.get("/cases/reasons")
          .then((r) => setReasons(r.data))
          .catch(() => {});
      }
    }
    // eslint-disable-next-line
  }, [open]);

  // Disable types based on order state
  const canCancel = order && !["shipped", "refunded", "cancelled"].includes(order.status);
  const canReturn = order && ["paid", "shipped", "partially_refunded"].includes(order.status);
  const canINR = order && !["refunded", "cancelled"].includes(order.status);

  const uploadOne = async (f) => {
    if (attachments.length >= MAX_ATTACHMENTS) {
      toast.error(`Maximum ${MAX_ATTACHMENTS} attachments`);
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      toast.error(`${f.name} is over 5 MB`);
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/cases/upload-attachment", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setAttachments((a) => [...a, data]);
    } catch (err) {
      toast.error(err.response?.data?.detail || `Upload of ${f.name} failed`);
    } finally {
      setUploading(false);
    }
  };

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    for (const f of files) {
      // eslint-disable-next-line no-await-in-loop
      await uploadOne(f);
    }
    if (fileRef.current) fileRef.current.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!type || !reason) {
      toast.error("Choose a case type and reason");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/cases", {
        order_id: order.order_id,
        type,
        reason,
        comments: comments.trim() || null,
        attachments,
      });
      toast.success(`Case #${data.case_number} opened`);
      onCreated?.(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not open case");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#F9F6F0] rounded-none border-[#E5E1D8] max-w-2xl w-[95vw] max-h-[92vh] overflow-y-auto"
        data-testid="new-case-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-serif text-3xl">Open a case</DialogTitle>
          <DialogDescription className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">
            Order #{order?.order_id}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-5" data-testid="new-case-form">
          {/* Step 1 — Type */}
          <div>
            <p className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60 mb-2">1 · Choose case type</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {(["return", "cancellation", "inr"]).map((k) => {
                const allowed = k === "return" ? canReturn : k === "cancellation" ? canCancel : canINR;
                return (
                  <button
                    type="button"
                    key={k}
                    disabled={!allowed}
                    onClick={() => { setType(k); setReason(""); }}
                    data-testid={`case-type-${k}`}
                    className={`border p-3 text-left transition-colors ${
                      type === k
                        ? "border-[#7A2021] bg-white"
                        : "border-[#E5E1D8] hover:border-[#1A1714]"
                    } ${!allowed ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}`}
                  >
                    <p className="font-serif text-lg text-[#1A1714]">{TYPE_META[k].label}</p>
                    <p className="font-mono text-[9px] uppercase tracking-editorial text-[#1A1714]/60 mt-1">
                      {TYPE_META[k].intro}
                    </p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Step 2 — Reason */}
          {type && (
            <div>
              <p className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60 mb-2">2 · Reason *</p>
              <select
                required
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                data-testid="case-reason-select"
                className="w-full h-11 border border-[#E5E1D8] rounded-none bg-white px-3 font-serif text-base"
              >
                <option value="">Choose a reason…</option>
                {(reasons[type] || []).map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>
          )}

          {/* Step 3 — Comments */}
          {type && (
            <div>
              <p className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60 mb-2">3 · Additional comments (optional)</p>
              <Textarea
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                placeholder="Give us more context so we can help you faster…"
                data-testid="case-comments"
                className="rounded-none min-h-24 border-[#E5E1D8]"
              />
            </div>
          )}

          {/* Step 4 — Attachments */}
          {type && (
            <div>
              <p className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60 mb-2">
                4 · Photos or documents (optional · up to {MAX_ATTACHMENTS})
              </p>
              <div className="flex flex-wrap gap-2 mb-2">
                {attachments.map((a, idx) => (
                  <AttachmentChip
                    key={a.path}
                    att={a}
                    onRemove={() => setAttachments((arr) => arr.filter((_, i) => i !== idx))}
                  />
                ))}
              </div>
              <input
                ref={fileRef}
                type="file"
                multiple
                className="hidden"
                accept="image/jpeg,image/png,image/gif,image/webp,application/pdf"
                onChange={onFiles}
                data-testid="case-file-input"
              />
              <Button
                type="button"
                variant="outline"
                disabled={uploading || attachments.length >= MAX_ATTACHMENTS}
                onClick={() => fileRef.current?.click()}
                data-testid="case-upload-btn"
                className="rounded-none border-[#1A1714] font-mono text-[10px] uppercase tracking-editorial"
              >
                <Upload size={12} className="mr-2" /> {uploading ? "Uploading…" : "Add file"}
              </Button>
              <p className="mt-1 font-mono text-[9px] uppercase tracking-editorial text-[#1A1714]/40">
                JPG · PNG · GIF · WEBP · PDF · ≤ 5 MB each
              </p>
            </div>
          )}

          <div className="flex gap-3 pt-2 border-t border-[#E5E1D8]">
            <Button
              type="submit"
              disabled={saving || !type || !reason}
              data-testid="case-submit"
              className="flex-1 rounded-none bg-[#1A1714] text-[#F9F6F0] hover:bg-[#7A2021] font-mono text-xs uppercase tracking-editorial"
            >
              {saving ? "Opening case…" : "Open case"}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              data-testid="case-cancel"
              className="rounded-none border-[#1A1714] font-mono text-xs uppercase tracking-editorial"
            >
              Cancel
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
